import React, { useCallback, useEffect, useState } from "react";
import { Button, Form, Row, Col, Input, Container } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faDownload, faCheck } from "@fortawesome/free-solid-svg-icons";
import { usePexelsCore } from "../../../cores/pexels";
import { PostImage } from "../../../cores/socialPost/interfaces/postImage";
import { v4 } from "uuid";
import { getAspectRatio, getBase64 } from "../../../cores/utils";
import InternalModal from "../../layout/InternalModal";
import "./ImageBankModal.scss";

interface Props {
  show: boolean;
  onHide: () => void;
  onImageSelect: (images: PostImage[]) => void;
}

export const ImageBankModal: React.FC<Props> = ({ show, onHide, onImageSelect }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPhotos, setSelectedPhotos] = useState<Set<number>>(new Set());
  const [downloading, setDownloading] = useState<Set<number>>(new Set());
  
  const {
    photos,
    loading,
    error,
    hasMore,
    searchPhotos,
    loadMorePhotos,
    loadCuratedPhotos,
    downloadPhoto,
    clearPhotos
  } = usePexelsCore();

  // Cargar imágenes curadas al abrir el modal
  useEffect(() => {
    if (show) {
      loadCuratedPhotos();
    } else {
      clearPhotos();
      setSelectedPhotos(new Set());
    }
  }, [show, loadCuratedPhotos, clearPhotos]);

  const handleSearch = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      searchPhotos(searchQuery.trim());
    }
  }, [searchQuery, searchPhotos]);

  const handlePhotoSelect = useCallback((photoId: number) => {
    setSelectedPhotos(prev => {
      const newSet = new Set(prev);
      if (newSet.has(photoId)) {
        newSet.delete(photoId);
      } else {
        newSet.add(photoId);
      }
      return newSet;
    });
  }, []);

  const handleDownloadSelected = useCallback(async () => {
    if (selectedPhotos.size === 0) return;

    setDownloading(new Set(selectedPhotos));
    
    try {
      const selectedPhotoObjects = photos.filter(photo => selectedPhotos.has(photo.id));
      const downloadedImages: PostImage[] = [];

      for (const photo of selectedPhotoObjects) {
        try {
          const blob = await downloadPhoto(photo);

          // Convertir el blob a data URL (image/jpeg) para poder enviarlo correctamente al backend
          const dataUrl = (await getBase64(blob)) as string;

          // Crear objeto de imagen para obtener dimensiones
          const img = new Image();
          img.onload = () => {
            const aspectRatio = getAspectRatio(img.width, img.height);
            const postImage: PostImage = {
              id: v4(),
              lastModified: new Date().getTime(),
              name: `pexels-${photo.id}.jpg`,
              size: blob.size,
              type: "image/jpeg",
              data: dataUrl,
              width: img.width,
              height: img.height,
              aspectRatio,
            };
            downloadedImages.push(postImage);
          };
          img.src = dataUrl;
        } catch (error) {
          console.error("Error downloading photo:", photo.id, error);
        }
      }

      // Esperar un poco para que se procesen las imágenes
      setTimeout(() => {
        onImageSelect(downloadedImages);
        onHide();
      }, 1000);

    } catch (error) {
      console.error('Error downloading images:', error);
    } finally {
      setDownloading(new Set());
    }
  }, [selectedPhotos, photos, downloadPhoto, onImageSelect, onHide]);

  const handleLoadMore = useCallback(() => {
    if (searchQuery.trim()) {
      searchPhotos(searchQuery.trim(), false);
    } else {
      loadMorePhotos();
    }
  }, [searchQuery, searchPhotos, loadMorePhotos]);

  return (
    <InternalModal active={show} onClose={onHide}>
      <Container className="image-bank-modal" fluid>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h4 className="mb-0">
                <FontAwesomeIcon icon={faDownload} className="me-2" />
                Banco de Imágenes
              </h4>
            </div>
        
        {/* Barra de búsqueda */}
        <div className="search-bar mb-3">
          <Form onSubmit={handleSearch}>
            <div className="d-flex">
              <Input
                type="text"
                placeholder="Buscar imágenes..."
                value={searchQuery}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
                className="me-2"
              />
              <Button 
                type="submit" 
                color="secondary"
                disabled={loading}
              >
                <FontAwesomeIcon icon={faSearch} />
              </Button>
            </div>
          </Form>
        </div>

        {/* Contenido de la galería */}
        <div className="gallery-content" style={{ height: '500px', overflowY: 'auto' }}>
          {error && (
            <div className="alert alert-danger m-3">
              {error}
            </div>
          )}

          {photos.length === 0 && !loading && (
            <div className="text-center p-5 text-muted">
              <FontAwesomeIcon icon={faSearch} size="3x" className="mb-3" />
              <p>No se encontraron imágenes</p>
            </div>
          )}

                 <Row className="g-2 p-3">
                   {photos.map((photo) => (
                     <Col xs={12} sm={6} md={4} lg={3} key={photo.id}>
                <div 
                  className={`photo-item ${selectedPhotos.has(photo.id) ? 'selected' : ''}`}
                  onClick={() => handlePhotoSelect(photo.id)}
                >
                  <div className="photo-wrapper">
                    <img
                      src={photo.src.medium}
                      alt={photo.alt}
                      loading="lazy"
                      className="photo-image"
                    />
                    <div className="photo-overlay">
                      {selectedPhotos.has(photo.id) && (
                        <div className="selected-indicator">
                          <FontAwesomeIcon icon={faCheck} />
                        </div>
                      )}
                      {downloading.has(photo.id) && (
                        <div className="downloading-indicator">
                          <div className="spinner-border spinner-border-sm" role="status">
                            <span className="sr-only">Cargando...</span>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="photo-info">
                      <small className="text-white">
                        {photo.photographer}
                      </small>
                    </div>
                  </div>
                </div>
              </Col>
            ))}
          </Row>

          {loading && (
            <div className="text-center p-3">
              <div className="spinner-border" role="status">
                <span className="sr-only">Cargando...</span>
              </div>
              <p className="mt-2">Cargando imágenes...</p>
            </div>
          )}

          {hasMore && !loading && photos.length > 0 && (
            <div className="text-center p-3">
              <Button color="outline-primary" onClick={handleLoadMore}>
                Cargar más imágenes
              </Button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="d-flex justify-content-between align-items-center mt-3 pt-3 border-top">
          <div>
            {selectedPhotos.size > 0 && (
              <span className="text-muted">
                {selectedPhotos.size} imagen{selectedPhotos.size !== 1 ? 'es' : ''} seleccionada{selectedPhotos.size !== 1 ? 's' : ''}
              </span>
            )}
          </div>
          <div>
            <Button color="secondary" onClick={onHide} className="me-2">
              Cancelar
            </Button>
            <Button 
              color="primary" 
              onClick={handleDownloadSelected}
              disabled={selectedPhotos.size === 0 || downloading.size > 0}
            >
              {downloading.size > 0 ? (
                <>
                  <div className="spinner-border spinner-border-sm me-2" role="status">
                    <span className="sr-only">Cargando...</span>
                  </div>
                  Descargando...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faDownload} className="me-2" />
                  Descargar Seleccionadas ({selectedPhotos.size})
                </>
              )}
            </Button>
          </div>
        </div>
      </Container>
    </InternalModal>
  );
};
